import { useState } from 'react';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

/* 42px padrão e 60px large vêm da especificação; sm fica em 36 para ação dentro
   de tabela e barra de ferramentas, onde 42 empurra a linha. */
const sizes: Record<ButtonSize, CSSProperties> = {
    sm: { height: 36, padding: '0 12px', fontSize: 13.5 },
    md: { height: 42, padding: '0 16px', fontSize: 15 },
    lg: { height: 60, padding: '0 24px', fontSize: 18 },
};

const variants: Record<ButtonVariant, CSSProperties> = {
    /* Tinta da ação vem do token: o verde da Fórmula MED pede tinta escura (11,9:1),
       e branco fixo dava 1,5:1. */
    primary: { background: 'var(--action-primary)', color: 'var(--action-primary-fg)', border: '1px solid transparent' },
    secondary: { background: 'var(--surface)', color: 'var(--neutral-700)', border: '1px solid var(--border-strong)' },
    tertiary: { background: 'var(--surface-secondary)', color: 'var(--neutral-700)', border: '1px solid transparent' },
    ghost: { background: 'transparent', color: 'var(--neutral-700)', border: '1px solid transparent' },
    danger: { background: 'var(--danger)', color: '#fff', border: '1px solid transparent' },
    link: { background: 'transparent', color: 'var(--text-link)', border: '1px solid transparent', padding: 0, height: 'auto' },
};

const hovers: Record<ButtonVariant, string> = {
    primary: 'var(--action-primary-hover)',
    secondary: 'var(--surface-secondary)',
    tertiary: 'var(--surface-hover)',
    ghost: 'var(--surface-secondary)',
    danger: 'var(--danger-strong)',
    link: 'transparent',
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    iconLeft?: ReactNode;
    iconRight?: ReactNode;
    /** Mostra o giro e desabilita o botão. */
    loading?: boolean;
    fullWidth?: boolean;
    type?: 'button' | 'submit' | 'reset';
}

export function Button({
    variant = 'primary', size = 'md', iconLeft, iconRight, loading = false, disabled = false,
    fullWidth = false, children, onClick, type = 'button', style, ...rest
}: ButtonProps) {
    const [hover, setHover] = useState(false);
    const [active, setActive] = useState(false);
    const v = variants[variant] ?? variants.primary;
    const s = sizes[size] ?? sizes.md;
    const off = disabled || loading;

    return (
        <button
            type={type}
            disabled={off}
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => { setHover(false); setActive(false); }}
            onMouseDown={() => setActive(true)}
            onMouseUp={() => setActive(false)}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                fontFamily: 'var(--font-ui)', fontWeight: 600, letterSpacing: 'var(--tracking-snug)',
                borderRadius: variant === 'link' ? 4 : 'var(--radius-control)',
                cursor: off ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                width: fullWidth ? '100%' : undefined,
                transition: 'var(--transition-control), transform var(--dur-fast) var(--ease-standard)',
                opacity: off ? 0.5 : 1,
                transform: active && !off ? 'scale(0.985)' : 'none',
                ...s, ...v,
                background: hover && !off
                    ? (variant === 'primary' && active ? 'var(--action-primary-pressed)' : hovers[variant])
                    : v.background,
                textDecoration: variant === 'link' && hover ? 'underline' : 'none',
                ...style,
            }}
            {...rest}
        >
            {loading && (
                <span aria-hidden="true" style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', animation: 'vec-spin .7s linear infinite', opacity: 0.8 }}>
                    {/* A regra do giro nasce com o giro. Antes ficava solta dentro
                        de TODO botão: dezenas de <style> iguais na página, e o
                        texto do CSS entrando no conteúdo do próprio botão. */}
                    <style>{'@keyframes vec-spin{to{transform:rotate(360deg)}}'}</style>
                </span>
            )}
            {!loading && iconLeft}
            {children}
            {iconRight}
        </button>
    );
}
